/**
 * useGlobalStyles — Local state management for the page editor's global theme styles.
 *
 * Manages brand color, font family, button style, and section spacing.
 * Persists draft to localStorage alongside (but separate from) section drafts.
 * Produces theme values in Storefront Theme DocType format (HSL for colors).
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react'
import { hexToHsl, hslToHex, DEFAULT_GLOBAL_STYLES } from './theme-utils'

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface GlobalStyles {
  /** Hex color for UI display, e.g. "#0ea5e9" */
  brandColor: string
  /** Font key, e.g. "system", "sarabun" */
  fontFamily: string
  /** Button style key: "rounded-md" | "pill" | "square" */
  buttonStyle: string
  /** Section spacing key: "compact" | "normal" | "relaxed" */
  sectionSpacing: string
  /** Page-level max width in px (undefined = full width) */
  pageMaxWidth?: number
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function themeDraftKey(pageId: string, tenantId: string) {
  return `page-editor-theme-draft-${tenantId}-${pageId}`
}

function loadThemeDraft(pageId: string, tenantId: string): GlobalStyles | null {
  try {
    const raw = localStorage.getItem(themeDraftKey(pageId, tenantId))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    if (!parsed || typeof parsed !== 'object') return null
    return parsed as GlobalStyles
  } catch {
    return null
  }
}

function saveThemeDraft(pageId: string, tenantId: string, styles: GlobalStyles) {
  try {
    localStorage.setItem(themeDraftKey(pageId, tenantId), JSON.stringify(styles))
  } catch {
    // localStorage full or unavailable — ignore
  }
}

/**
 * Convert server theme values (from Storefront Theme DocType) to GlobalStyles.
 * Server format: { "color.primary": "199 89% 48%", "font.family": "system", ... }
 */
function serverThemeToStyles(
  values: Record<string, string> | null | undefined,
): GlobalStyles {
  if (!values) return { ...DEFAULT_GLOBAL_STYLES }
  const raw = values['page.maxWidth']
  const pageMaxWidth = raw ? parseInt(raw, 10) : undefined
  return {
    brandColor: values['color.primary']
      ? hslToHex(values['color.primary'])
      : DEFAULT_GLOBAL_STYLES.brandColor,
    fontFamily: values['font.family'] || DEFAULT_GLOBAL_STYLES.fontFamily,
    buttonStyle: values['button.style'] || DEFAULT_GLOBAL_STYLES.buttonStyle,
    sectionSpacing: values['section.spacing'] || DEFAULT_GLOBAL_STYLES.sectionSpacing,
    pageMaxWidth: Number.isFinite(pageMaxWidth) ? pageMaxWidth : undefined,
  }
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export function useGlobalStyles(
  pageId: string,
  tenantId: string,
  initialServerTheme: Record<string, string> | null | undefined,
) {
  const hydratedRef = useRef(false)
  const saveTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined)

  // Derive initial styles from server theme
  const initialStyles = useMemo(
    () => serverThemeToStyles(initialServerTheme),
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only compute once from initial server data
    [],
  )

  // Initialize: try localStorage draft first, then server theme, then defaults
  const [styles, setStyles] = useState<GlobalStyles>(() => {
    if (!tenantId) return initialServerTheme ? serverThemeToStyles(initialServerTheme) : { ...DEFAULT_GLOBAL_STYLES }
    const draft = loadThemeDraft(pageId, tenantId)
    if (draft) {
      hydratedRef.current = true
      return draft
    }
    if (initialServerTheme) {
      hydratedRef.current = true
      return serverThemeToStyles(initialServerTheme)
    }
    return { ...DEFAULT_GLOBAL_STYLES }
  })

  // Sync when initialServerTheme arrives (null → populated) — only once
  useEffect(() => {
    if (hydratedRef.current) return
    if (!initialServerTheme) return
    hydratedRef.current = true
    setStyles((prev) => {
      // Only update if still at defaults (no user edits)
      if (JSON.stringify(prev) === JSON.stringify(DEFAULT_GLOBAL_STYLES)) {
        return serverThemeToStyles(initialServerTheme)
      }
      return prev
    })
  }, [initialServerTheme])

  // Auto-save to localStorage on change (debounced 500ms)
  useEffect(() => {
    clearTimeout(saveTimerRef.current)
    if (!tenantId) return // Don't save drafts without tenant context
    saveTimerRef.current = setTimeout(() => saveThemeDraft(pageId, tenantId, styles), 500)
    return () => clearTimeout(saveTimerRef.current)
  }, [styles, pageId, tenantId])

  // --- Updaters ---

  const setBrandColor = useCallback((color: string | undefined) => {
    setStyles((prev) => ({ ...prev, brandColor: color || DEFAULT_GLOBAL_STYLES.brandColor }))
  }, [])

  const setFontFamily = useCallback((font: string) => {
    setStyles((prev) => ({ ...prev, fontFamily: font }))
  }, [])

  const setButtonStyle = useCallback((style: string) => {
    setStyles((prev) => ({ ...prev, buttonStyle: style }))
  }, [])

  const setSectionSpacing = useCallback((spacing: string) => {
    setStyles((prev) => ({ ...prev, sectionSpacing: spacing }))
  }, [])

  const setPageMaxWidth = useCallback((width: number | undefined) => {
    setStyles((prev) => ({ ...prev, pageMaxWidth: width }))
  }, [])

  // --- Derived ---

  /**
   * Convert to Storefront Theme values_json format (HSL for colors).
   */
  const toThemeValues = useCallback((): Record<string, string> => {
    const values: Record<string, string> = {
      'color.primary': hexToHsl(styles.brandColor),
      'font.family': styles.fontFamily,
      'button.style': styles.buttonStyle,
      'section.spacing': styles.sectionSpacing,
    }
    if (styles.pageMaxWidth) {
      values['page.maxWidth'] = String(styles.pageMaxWidth)
    }
    return values
  }, [styles])

  const isDirty = useMemo(() => {
    return JSON.stringify(styles) !== JSON.stringify(initialStyles)
  }, [styles, initialStyles])

  const clearDraft = useCallback(() => {
    if (!tenantId) return
    try {
      localStorage.removeItem(themeDraftKey(pageId, tenantId))
    } catch {
      // ignore
    }
  }, [pageId, tenantId])

  return {
    styles,
    setBrandColor,
    setFontFamily,
    setButtonStyle,
    setSectionSpacing,
    setPageMaxWidth,
    toThemeValues,
    isDirty,
    clearDraft,
  }
}
